import { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Form, Table, Spinner, Button, ProgressBar } from "react-bootstrap";
import { BiRefresh, BiStore, BiCheckCircle, BiXCircle, BiTime, BiTachometer, BiDollar, BiX } from "react-icons/bi";
import {
  getPlatformShopsInsights,
  getRegisteredShopsOverTime,
  getShopFulfillmentInsights,
} from "../api/getapi";
import { formatNumber } from "../Utilities/numbers";
import { ECHART_COLORS } from "../Utilities/echartsTheme";
import StatCard from "../Components/DashboardCards/StatCard/StatCard";
import EChartsDoughnut from "../Components/Charts/EChartsDoughnut";
import EChartsVerticalBar from "../Components/Charts/EChartsVerticalBar";

const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 0.8s linear infinite; display: inline-block; }`;

const timePeriods = [
  { value: "all-time", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "last-year", label: "Last Year" },
];

const StoresDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [overTime, setOverTime] = useState(null);
  const [fulfillment, setFulfillment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("all-time");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, overTimeRes, fulfillmentRes] = await Promise.all([
        getPlatformShopsInsights(),
        getRegisteredShopsOverTime(),
        getShopFulfillmentInsights({ timePeriod }),
      ]);
      setSummary(summaryRes.data);
      setOverTime(overTimeRes.data);
      setFulfillment(fulfillmentRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const statusChartData = useMemo(() => {
    if (!summary) return {};
    return {
      Active: summary.activeShops || 0,
      Deactivated: summary.deactivatedShops || 0,
      "Awaiting Activation": summary.awaitingActivationShops || 0,
      "On Trial": summary.shopsOnTrial || 0,
      "On Subscription": summary.shopsOnSubscription || 0,
      "Cancelled Sub.": summary.shopsInCancelledSubscription || 0,
    };
  }, [summary]);

  const topFulfillment = useMemo(
    () =>
      (fulfillment || [])
        .slice()
        .sort((a, b) => Number(b.attempted) - Number(a.attempted))
        .slice(0, 15),
    [fulfillment]
  );

  const fulfillmentStats = useMemo(() => {
    const totalAttempted = topFulfillment.reduce((s, r) => s + Number(r.attempted), 0);
    const totalFulfilled = topFulfillment.reduce((s, r) => s + Number(r.fulfilled), 0);
    const totalCancelled = topFulfillment.reduce((s, r) => s + Number(r.cancelled), 0);
    const totalReturned = topFulfillment.reduce((s, r) => s + Number(r.returned), 0);
    const totalMoved = topFulfillment.reduce((s, r) => s + Number(r.moved_to_mall), 0);
    const overallRate = totalAttempted ? Math.round((totalFulfilled / totalAttempted) * 100) : 0;
    return { totalAttempted, totalFulfilled, totalCancelled, totalReturned, totalMoved, overallRate };
  }, [topFulfillment]);

  return (
    <Container fluid className="px-xl-5">
      <style>{spinKeyframes}</style>
      <Row className="py-2 align-items-center">
        <Col md="6">
          <h5 className="fw-bold">Stores Dashboard</h5>
          <p className="text-muted mb-0">Shop registrations, status, and fulfillment over time.</p>
        </Col>
        <Col md="6" className="text-md-end mt-3 mt-md-0">
          <Button
            variant="light"
            size="sm"
            className="me-2 border"
            onClick={fetchData}
            disabled={loading}
          >
            <BiRefresh size={20} className={loading ? "spin" : ""} />
          </Button>
          <Form.Select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            style={{ width: 180, display: "inline-block" }}
          >
            {timePeriods.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {loading && (
        <div className="text-center mt-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && summary && (
        <>
          {/* KPI Stat Cards */}
          <Row className="g-3 mt-1 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Total Shops"
                count={formatNumber(summary.totalShops)}
                icon={<BiStore />}
                color={ECHART_COLORS.primary}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Active Shops"
                count={formatNumber(summary.activeShops)}
                icon={<BiCheckCircle />}
                color={ECHART_COLORS.success}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="On Trial"
                count={formatNumber(summary.shopsOnTrial)}
                icon={<BiTachometer />}
                color={ECHART_COLORS.accent}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="On Subscription"
                count={formatNumber(summary.shopsOnSubscription)}
                icon={<BiDollar />}
                color={ECHART_COLORS.purple}
              />
            </Col>
          </Row>

          {/* Secondary KPIs */}
          <Row className="g-3 mb-4">
            <Col md="4" lg="3" sm="12" xl="3">
              <StatCard
                title="Deactivated"
                count={formatNumber(summary.deactivatedShops)}
                icon={<BiXCircle />}
                color={ECHART_COLORS.danger}
              />
            </Col>
            <Col md="4" lg="3" sm="12" xl="3">
              <StatCard
                title="Awaiting Activation"
                count={formatNumber(summary.awaitingActivationShops)}
                icon={<BiTime />}
                color={ECHART_COLORS.accentLight}
              />
            </Col>
            <Col md="4" lg="3" sm="12" xl="3">
              <StatCard
                title="Cancelled Subscription"
                count={formatNumber(summary.shopsInCancelledSubscription)}
                icon={<BiX />}
                color={ECHART_COLORS.slate}
              />
            </Col>
            <Col md="4" lg="3" sm="12" xl="3">
              <Card className="border-0 shadow-sm h-100 p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="text-secondary fs-6 fw-bold m-0">Fulfillment Rate</p>
                    <p className="text-black fs-3 fw-bold m-0">{fulfillmentStats.overallRate}%</p>
                  </div>
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 48,
                      height: 48,
                      backgroundColor: `${fulfillmentStats.overallRate >= 80 ? ECHART_COLORS.success : fulfillmentStats.overallRate >= 50 ? ECHART_COLORS.accentLight : ECHART_COLORS.danger}20`,
                      color: fulfillmentStats.overallRate >= 80 ? ECHART_COLORS.success : fulfillmentStats.overallRate >= 50 ? ECHART_COLORS.accentLight : ECHART_COLORS.danger,
                    }}
                  >
                    <BiTachometer size={24} />
                  </div>
                </div>
                <ProgressBar
                  now={fulfillmentStats.overallRate}
                  variant={fulfillmentStats.overallRate >= 80 ? "success" : fulfillmentStats.overallRate >= 50 ? "warning" : "danger"}
                  style={{ height: 6, marginTop: 8 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="5" sm="12" xl="5">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Shop Status Distribution</h6>
                <div style={{ height: 300, overflow: "hidden" }}>
                  <EChartsDoughnut
                    data={statusChartData}
                    colors={[ECHART_COLORS.success, ECHART_COLORS.danger, ECHART_COLORS.accentLight, ECHART_COLORS.accent, ECHART_COLORS.purple, ECHART_COLORS.slate]}
                  />
                </div>
              </Card>
            </Col>
            <Col md="12" lg="7" sm="12" xl="7">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Registered Shops Over Time</h6>
                {overTime && (
                  <div style={{ height: 300, overflow: "hidden" }}>
                    <EChartsVerticalBar data={overTime} label="Shops" />
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Shop Fulfillment Table */}
          <Row className="g-3 mb-4">
            <Col md="12">
              <Card className="border-0 shadow-sm p-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                  <h6 className="fw-bold m-0">Shop Fulfillment</h6>
                  <div className="d-flex flex-wrap gap-3 text-muted" style={{ fontSize: "0.85rem" }}>
                    <span><strong className="text-dark">{formatNumber(fulfillmentStats.totalAttempted)}</strong> Orders Received</span>
                    <span><strong className="text-success">{formatNumber(fulfillmentStats.totalFulfilled)}</strong> Fulfilled</span>
                    <span><strong className="text-danger">{formatNumber(fulfillmentStats.totalCancelled)}</strong> Cancelled</span>
                    <span><strong className="text-warning">{formatNumber(fulfillmentStats.totalReturned)}</strong> Returned</span>
                    <span><strong className="text-primary">{formatNumber(fulfillmentStats.totalMoved)}</strong> Moved to Mall</span>
                  </div>
                </div>
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center" style={{ width: 40 }}>#</th>
                        <th>Shop</th>
                        <th className="text-end" style={{ width: 110 }}>Orders Received</th>
                        <th className="text-end" style={{ width: 90 }}>Fulfilled</th>
                        <th className="text-end" style={{ width: 90 }}>Cancelled</th>
                        <th className="text-end" style={{ width: 90 }}>Returned</th>
                        <th className="text-end" style={{ width: 100 }}>Moved</th>
                        <th style={{ width: 160 }} title="Delivered orders relative to orders received">
                          Fulfillment Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topFulfillment.map((row, i) => {
                        const attempted = Number(row.attempted) || 0;
                        const fulfilled = Number(row.fulfilled) || 0;
                        const rate = attempted ? Math.round((fulfilled / attempted) * 100) : 0;
                        return (
                          <tr key={row.shop_id}>
                            <td className="text-center text-muted">{i + 1}</td>
                            <td>
                              <div className="fw-medium">{row.shop_name}</div>
                              <small className="text-muted">
                                {formatNumber(row.cancelled)} cancelled · {formatNumber(row.returned)} returned · {formatNumber(row.moved_to_mall)} moved
                              </small>
                            </td>
                            <td className="text-end fw-bold">{formatNumber(attempted)}</td>
                            <td className="text-end text-success fw-bold">{formatNumber(fulfilled)}</td>
                            <td className="text-end text-danger">{formatNumber(row.cancelled)}</td>
                            <td className="text-end text-warning">{formatNumber(row.returned)}</td>
                            <td className="text-end text-primary">{formatNumber(row.moved_to_mall)}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <ProgressBar
                                  now={rate}
                                  variant={rate >= 80 ? "success" : rate >= 50 ? "warning" : "danger"}
                                  style={{ height: 6, flex: 1 }}
                                />
                                <small className="fw-bold" style={{ minWidth: 36 }}>{rate}%</small>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {topFulfillment.length === 0 && (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-3">
                            No fulfillment data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default StoresDashboard;
